import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import JSON5 from "json5";
import React, { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { JSONEditor } from "@/common/components/CodeEditor/JSONEditor";
import FormInput from "@/common/components/Form/FormInput";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import {
  FeatureConfigVariant,
  FeatureConfigVariantSchema,
} from "@reflag/shared/featureConfigAPI";
import { z } from "@reflag/shared/utils/zod";

type Props = {
  variant?: FeatureConfigVariant;
  existingKeys?: string[];
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FeatureConfigVariant) => void;
};

function tryParseJSON(str: string) {
  if (!str.length) {
    return { success: true, value: undefined };
  }

  try {
    return { success: true, value: JSON5.parse(str) };
  } catch {
    return { success: false, value: undefined };
  }
}

export const FeatureConfigModal = ({
  variant,
  existingKeys,
  isOpen,
  onClose,
  onSubmit,
}: Props) => {
  const extendedSchema = useMemo(
    () =>
      FeatureConfigVariantSchema.omit({ payload: true })
        .extend({
          payload: z.string().refine((str) => {
            return tryParseJSON(str).success;
          }, 'Payload must be valid JSON: "string", number, boolean, null, object, or array.'),
        })
        .refine(({ key }) => !existingKeys || !existingKeys.includes(key), {
          message: "Key must be unique",
          path: ["key"],
        })
        .transform(({ key, payload }) => ({
          key,
          payload: tryParseJSON(payload).value,
        })),
    [existingKeys],
  );

  const form = useForm<z.input<typeof extendedSchema>>({
    resolver: zodResolver(extendedSchema),
    defaultValues: {
      key: variant?.key || "",
      payload: JSON.stringify(variant?.payload, null, 2) || "",
    },
  });

  const handleSubmit = form.handleSubmit((data: FeatureConfigVariant) => {
    onSubmit(data);
  });

  return (
    <FormProvider {...form}>
      <Modal
        closeOnEsc={false}
        closeOnOverlayClick={false}
        isOpen={isOpen}
        size="4xl"
        onClose={onClose}
        onCloseComplete={form.reset}
      >
        <ModalOverlay />
        <ModalContent as="form" onSubmit={handleSubmit}>
          <ModalHeader>
            {variant ? "Edit" : "Create"} config variant
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" flexDirection="column" gap={4}>
            <FormInput label="Key" name="key" />
            <ManagedFormControl
              description="Can be any valid JSON value like object, array, string, number, boolean and null."
              name="payload"
              render={({ field }) => (
                <JSONEditor
                  initialValue={field.value}
                  label="Payload (optional)"
                  onChange={field.onChange}
                />
              )}
            />
            <FormRootError />
          </ModalBody>
          <ModalFooter>
            {form.formState.isDirty && (
              <Text color="dimmed" fontStyle="italic" mr={3}>
                You have unsaved changes
              </Text>
            )}
            <Button mr={3} variant="ghostDimmed" onClick={onClose}>
              Cancel
            </Button>
            <FormSubmit>{variant ? "Save" : "Add"}</FormSubmit>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FormProvider>
  );
};
